import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/DropdownMenu';
import { SidebarMenuButton, useHoverExpandLock } from '@shared/components/ui/sidebar';
import { useUIStore } from '@shared/stores/ui.store';

interface StartMenuProps {
  /** Navigate within the current tab (does NOT open a new tab). */
  onNavigate: (path: string) => void;
  /** Explicitly open a brand-new tab (the only entry point besides ⌘E). */
  onNewTab: () => void;
}

export function StartMenu({ onNavigate, onNewTab }: StartMenuProps) {
  const { requestNewAiCioChat, startMenuOpen, setStartMenuOpen } = useUIStore();
  const { t } = useTranslation();

  useHoverExpandLock(startMenuOpen);

  // Controlled so the `C` Quick-Create hotkey can open it (per the New CRM spec).
  return (
    <DropdownMenu open={startMenuOpen} onOpenChange={setStartMenuOpen}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton tooltip={t('start.trigger')}>
          <Plus />
          <span>{t('start.trigger')}</span>
        </SidebarMenuButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-48 rounded-xl">
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            // Per spec: "+ New Chat" redirects to the Main Chatbot page (new chat),
            // it does NOT open the side panel.
            requestNewAiCioChat();
            onNavigate('/ai-cio');
          }}
        >
          {t('start.newChat')}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            onNavigate('/wealth/investors/new');
          }}
        >
          {t('start.newInvestor')}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            // Opens the global Accounts page with the New Account panel expanded.
            onNavigate('/wealth/accounts?create=1');
          }}
        >
          {t('start.newAccount')}
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            onNewTab();
          }}
        >
          {t('start.newTab')}
          <span className="ms-auto text-xs text-muted-foreground">⌘E</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
